import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import * as productService from '../services/productService';

export interface Product {
  id: string;
  productName: string;
  groupName: string;
  unitPrice: number;
  description?: string;
}

export interface ProductGroup {
  id: string;
  name: string;
}

interface ProductContextType {
  products: Product[];
  productGroups: ProductGroup[];
  loading: boolean;
  error: string | null;
  addProduct: (product: Omit<Product, 'id'>) => Promise<void>;
  updateProduct: (id: string, updates: Partial<Product>) => Promise<void>;
  deleteProduct: (id: string) => Promise<void>;
  refreshProducts: () => Promise<void>;
  getProductsByGroup: (groupName: string) => Product[];
  getLicenseProducts: () => Product[];
  getImplementationProducts: () => Product[];
}

const ProductContext = createContext<ProductContextType | undefined>(undefined);

export const useProducts = () => {
  const context = useContext(ProductContext);
  if (!context) {
    throw new Error('useProducts must be used within a ProductProvider');
  }
  return context;
};

interface ProductProviderProps {
  children: ReactNode;
}

export const ProductProvider = ({ children }: ProductProviderProps) => {
  const [products, setProducts] = useState<Product[]>([]);
  const [productGroups, setProductGroups] = useState<ProductGroup[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch all products from Supabase
  const refreshProducts = async () => {
    try {
      setLoading(true);
      setError(null);
      const fetchedProducts = await productService.getAllProducts();
      const fetchedGroups = await productService.getProductGroups();
      setProducts(fetchedProducts);
      setProductGroups(fetchedGroups);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch products';
      setError(errorMessage);
      console.error('Error fetching products:', err);
    } finally {
      setLoading(false);
    }
  };

  // Load products on mount
  useEffect(() => {
    refreshProducts();
  }, []);

  const addProduct = async (product: Omit<Product, 'id'>) => {
    try {
      setError(null);
      const newProduct = await productService.createProduct(product);
      setProducts((prev) => [...prev, newProduct]);
      // Refresh groups in case a new group was added
      const fetchedGroups = await productService.getProductGroups();
      setProductGroups(fetchedGroups);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to create product';
      setError(errorMessage);
      console.error('Error creating product:', err);
      throw err;
    }
  };

  const updateProduct = async (id: string, updates: Partial<Product>) => {
    try {
      setError(null);
      const updatedProduct = await productService.updateProduct(id, updates);
      setProducts((prev) =>
        prev.map((p) => (p.id === id ? updatedProduct : p))
      );
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update product';
      setError(errorMessage);
      console.error('Error updating product:', err);
      throw err;
    }
  };

  const deleteProduct = async (id: string) => {
    try {
      setError(null);
      await productService.deleteProduct(id);
      setProducts((prev) => prev.filter((p) => p.id !== id));
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to delete product';
      setError(errorMessage);
      console.error('Error deleting product:', err);
      throw err;
    }
  };

  const getProductsByGroup = (groupName: string) => {
    return products.filter((p) => p.groupName === groupName);
  };

  const getLicenseProducts = () => {
    return products.filter(
      (p) =>
        p.groupName === 'Creatio Product License' ||
        p.groupName === 'Creatio Studio License' ||
        p.groupName === 'Evoqia Product License'
    );
  };

  const getImplementationProducts = () => {
    return products.filter(
      (p) =>
        p.groupName === 'Business Analysis' ||
        p.groupName === 'Creatio Development' ||
        p.groupName === 'Integrations' ||
        p.groupName === 'Portal Development'
    );
  };

  return (
    <ProductContext.Provider
      value={{
        products,
        productGroups,
        loading,
        error,
        addProduct,
        updateProduct,
        deleteProduct,
        refreshProducts,
        getProductsByGroup,
        getLicenseProducts,
        getImplementationProducts,
      }}
    >
      {children}
    </ProductContext.Provider>
  );
};